import { useContext } from "react";
import { Instagram, Linkedin, Facebook, ArrowUpRight } from "lucide-react";
import { StaxLogo, StaxMark } from "./Logo";
import { useCursorOn, setCursorOn } from "@/lib/cursorPref";
import { ContentCtx } from "@/lib/content";

const NAV = [
  { label: "About", href: "#about" },
  { label: "Portfolio", href: "#work" },
  { label: "Services", href: "#services" },
  { label: "Brand Kit", href: "#brand-kit" },
  { label: "Contact", href: "#contact" },
];

const SOCIALS = [
  { icon: Instagram, label: "Instagram", href: "https://instagram.com" },
  { icon: Linkedin, label: "LinkedIn", href: "https://linkedin.com" },
  { icon: Facebook, label: "Facebook", href: "https://facebook.com" },
];

export const Footer = () => {
  const cursorOn = useCursorOn();
  const content = useContext(ContentCtx);
  const nav = NAV.filter((l) => l.href !== "#work" || !!content?.sections?.work);

  return (
    <footer data-testid="site-footer" className="relative overflow-hidden bg-ink border-t-4 border-white/10 text-white">
      <div className="ink-dot absolute -right-24 -bottom-24 h-72 w-72 bg-staxmagenta opacity-70" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-8 py-16">
        <div className="flex flex-col lg:flex-row justify-between gap-12">
          <div>
            <StaxLogo dark size={48} />
            <p className="mt-4 max-w-xs font-mono2 text-xs uppercase tracking-[0.25em] text-white/50 leading-relaxed">
              Creative design &amp; marketing studio. Bold color. Smart strategy. Zero boring.
            </p>
            <div className="mt-6 flex gap-3">
              {SOCIALS.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`social-${s.label.toLowerCase()}`}
                  aria-label={s.label}
                  className="border-2 border-white/40 p-2.5 text-white hover:bg-staxyellow hover:text-ink hover:border-staxyellow transition-colors"
                >
                  <s.icon size={18} />
                </a>
              ))}
            </div>
          </div>

          <nav className="grid grid-cols-2 gap-x-16 gap-y-3">
            {nav.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="group inline-flex items-center gap-1.5 font-display uppercase text-lg text-white/80 hover:text-staxyellow transition-colors"
              >
                {l.label}
                <ArrowUpRight size={14} className="opacity-0 transition-opacity group-hover:opacity-100" />
              </a>
            ))}
          </nav>

          <div className="hidden lg:block">
            <StaxMark size={64} dark className="floaty" />
          </div>
        </div>

        <div className="mt-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-t-2 border-white/15 pt-6">
          <p className="font-mono2 text-xs uppercase tracking-[0.25em] text-white/50">
            © 2026 STAX Marketing LLC
          </p>
          <div className="flex items-center gap-2.5" data-testid="cursor-toggle">
            <span className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-white/50">
              Circle Cursor
            </span>
            <button
              role="switch"
              aria-checked={cursorOn}
              aria-label="Toggle circle cursor"
              data-testid="cursor-toggle-switch"
              onClick={() => setCursorOn(!cursorOn)}
              className={`relative h-5 w-10 shrink-0 border-2 transition-colors ${cursorOn ? "border-staxyellow bg-staxyellow" : "border-white/40 bg-transparent"}`}
            >
              <span
                className={`absolute top-0.5 h-3 w-3 transition-all ${cursorOn ? "left-[22px] bg-ink" : "left-0.5 bg-white/60"}`}
              />
            </button>
          </div>
          <p className="font-mono2 text-xs uppercase tracking-[0.25em] text-white/50">
            Made with good design <span className="text-staxyellow">+</span> too much color<span className="text-staxmagenta">.</span>
          </p>
        </div>
      </div>
    </footer>
  );
};
